import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "seller" || u.role === "admin" ? "/dashboard" : "/");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[#E2E8F0] rounded-3xl p-8">
        <div className="flex items-center gap-2 text-[#0EA5E9] mb-2"><Sparkles className="h-5 w-5" /></div>
        <h1 className="font-display text-3xl font-extrabold text-[#0F172A]">{t("auth.loginTitle")}</h1>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label>{t("auth.email")}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              data-testid="login-email" className="mt-1.5 rounded-xl" placeholder="tu@email.com" />
          </div>
          <div>
            <Label>{t("auth.password")}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              data-testid="login-password" className="mt-1.5 rounded-xl" placeholder="••••••••" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full rounded-full h-11 bg-[#0F172A] hover:bg-[#0EA5E9] text-white font-bold">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("auth.loginBtn")}
          </Button>
        </form>
        <p className="text-sm text-[#475569] mt-5 text-center">
          {t("auth.noAccount")} <Link to="/register" className="text-[#0EA5E9] font-semibold">{t("nav.register")}</Link>
        </p>
        <p className="text-xs text-[#94A3B8] mt-4 text-center bg-slate-50 rounded-xl p-3">{t("auth.demoHint")}</p>
      </motion.div>
    </div>
  );
}
